import {
  forwardRef,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

import { cn } from './cn';

const fieldBase = cn(
  'block w-full rounded-md border border-border-strong/80 bg-surface px-3.5 py-2 text-sm',
  'shadow-[inset_0_1px_2px_rgb(16_18_27_/_0.04)]',
  'placeholder:text-foreground-subtle',
  'transition-[border-color,box-shadow] duration-150 ease-out',
  'focus:border-brand focus:outline-none focus:shadow-glow-ring',
  'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-foreground-muted disabled:shadow-none',
  'invalid:border-danger',
  'aria-[invalid=true]:border-danger aria-[invalid=true]:focus:shadow-[0_0_0_4px_rgb(229_72_77_/_0.16)]',
);

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cn(fieldBase, 'h-10', className)} {...rest} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, rows = 3, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(fieldBase, 'min-h-[3rem] resize-y leading-relaxed', className)}
      {...rest}
    />
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select
        ref={ref}
        className={cn(
          fieldBase,
          'h-10 pe-9 bg-no-repeat appearance-none cursor-pointer',
          // Caret built from a data URI so we don't ship an SVG asset.
          "bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 20 20%22 fill=%22%239aa0b0%22><path fill-rule=%22evenodd%22 d=%22M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z%22 clip-rule=%22evenodd%22/></svg>')] bg-[length:1rem] bg-[position:right_0.75rem_center]",
          "[dir='rtl']:bg-[position:left_0.75rem_center] [dir='rtl']:pe-3 [dir='rtl']:ps-9",
          className,
        )}
        {...rest}
      >
        {children}
      </select>
    );
  },
);
